import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Download,
  Loader2,
  LogOut,
  Sparkles,
  Coffee,
  Clock,
  FileText,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { BuyMeACoffeeButton } from "@/components/BuyMeACoffeeButton";
import { useAuth } from "@/context/AuthContext";
import { openDownload, openDownloadAssets } from "@/utils/openDownload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The full member-facing resource catalog. Mirrors the public Resources page
// slugs so a 1-click download works for every asset the guest modal exposes.
const MEMBER_RESOURCES = [
  { slug: "florida-notary-master-checklist", title: "Florida Notary Master Checklist", kind: "PDF" },
  { slug: "10-steps-to-ron-video", title: "10 Steps to RON — Video Guide + Checklist", kind: "VIDEO + PDF" },
  { slug: "apostille-country-quick-reference", title: "Apostille Country Quick-Reference", kind: "PDF" },
  { slug: "loan-signing-document-glossary", title: "Loan Signing Document Glossary", kind: "PDF" },
  { slug: "ogo-operational-doctrine", title: "OGO Operational Doctrine Blueprint", kind: "PDF" },
  { slug: "florida-9-requirements", title: "Florida 9 Requirements", kind: "PDF" },
  { slug: "unlocking-business-deductions", title: "Unlocking Business Deductions (Ebook)", kind: "EBOOK" },
  { slug: "wills-estates-trusts-planning", title: "Wills, Estates & Trusts — Planning 101 + Sample Scripts", kind: "2-PDF PACK" },
  { slug: "elder-care-documents-checklist", title: "Essential Elder-Care Documents Checklist", kind: "PDF" },
  { slug: "clinical-notary-blueprint", title: "Clinical Notary Blueprint (Full Pack)", kind: "PDF + VIDEO + GRAPHICS" },
  { slug: "30-day-mobile-notary-roadmap-podcast", title: "30-Day Mobile Notary Roadmap Podcast", kind: "AUDIO" },
];

const RESOURCE_TITLE_BY_SLUG = Object.fromEntries(
  MEMBER_RESOURCES.map((r) => [r.slug, r.title])
);

export default function CommunityHubPage() {
  const { user, isLoading, isAuthed, authHeader, logout } = useAuth();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [busySlug, setBusySlug] = useState(null);

  const firstName = useMemo(
    () => (user?.name || "").trim().split(/\s+/)[0] || "friend",
    [user]
  );

  useEffect(() => {
    if (!isAuthed) return;
    (async () => {
      setLoadingHistory(true);
      try {
        const { data } = await axios.get(`${API}/community/downloads/my`, {
          headers: authHeader(),
        });
        setHistory(Array.isArray(data) ? data : []);
      } catch {
        setHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [isAuthed, authHeader]);

  const oneClickDownload = async (slug, title) => {
    setBusySlug(slug);
    try {
      const { data } = await axios.post(
        `${API}/community/downloads/log`,
        { slug },
        { headers: authHeader() }
      );
      const assets = Array.isArray(data?.download_assets)
        ? data.download_assets
        : [];
      const primary = data?.download_url;
      if (assets.length > 0) {
        const { openedInNewTab } = openDownloadAssets(assets, {
          onBlockedRest: (blocked) => {
            const first = blocked[0];
            toast.info("Additional file ready", {
              description: `Popup was blocked — click to open ${first.label}.`,
              action: {
                label: "Open",
                onClick: () => openDownload(first.url),
              },
              duration: 15000,
            });
          },
        });
        const extras = assets.length - 1;
        toast.success(openedInNewTab ? "Download started!" : "Opening download…", {
          description:
            extras > 0
              ? `${title} — plus ${extras} companion file${extras === 1 ? "" : "s"} opening in new tabs.`
              : title,
          id: `download-${slug}`,
        });
        setHistory((prev) => [
          { resource_slug: slug, downloaded_at: new Date().toISOString() },
          ...prev.filter((h) => h.resource_slug !== slug),
        ]);
      } else if (primary) {
        openDownload(primary);
        toast.success("Download started!", {
          description: title,
          id: `download-${slug}`,
        });
        setHistory((prev) => [
          { resource_slug: slug, downloaded_at: new Date().toISOString() },
          ...prev.filter((h) => h.resource_slug !== slug),
        ]);
      } else {
        toast.error("That resource isn't available yet — check back soon.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "We couldn't process that download. Please try again."
      );
    } finally {
      setBusySlug(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-slate-300 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
        <p className="text-sm">Loading your library…</p>
      </div>
    );
  }

  if (!isAuthed) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <PageHero
        eyebrow="My Community Hub"
        title={`Welcome back, ${firstName}.`}
        lead="Your personal library — every current and future OGO resource, one click away. No forms. No waiting."
      />

      {/* 1-click download grid */}
      <section className="py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-10">
            <SectionEyebrow className="mb-4">Free Downloads</SectionEyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight tracking-tight">
              One-click access to every resource.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed text-sm sm:text-base">
              Click any card to open the file in a new tab — we log the download
              to your profile so you can always come back and grab it again.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {MEMBER_RESOURCES.map((r) => (
              <button
                key={r.slug}
                type="button"
                onClick={() => oneClickDownload(r.slug, r.title)}
                disabled={busySlug === r.slug}
                data-testid={`community-download-${r.slug}`}
                className="group text-left border border-white/10 bg-[#1A2B45] p-5 sm:p-6 hover:border-[#D4AF37]/40 transition-colors flex flex-col disabled:opacity-60"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                    <FileText className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 border border-white/10 px-2 py-1">
                    {r.kind}
                  </span>
                </div>
                <h3 className="font-serif-display text-lg text-white leading-snug mb-4 flex-1">
                  {r.title}
                </h3>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  {busySlug === r.slug ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Starting…
                    </>
                  ) : (
                    <>
                      <Download className="w-3.5 h-3.5" />
                      Instant Download
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Download history */}
      <section className="py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-5 h-5 text-[#D4AF37]" />
            <SectionEyebrow>Your Recent Downloads</SectionEyebrow>
          </div>

          {loadingHistory ? (
            <div className="text-slate-400 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
            </div>
          ) : history.length === 0 ? (
            <div
              data-testid="community-history-empty"
              className="border border-dashed border-white/15 bg-[#0A1020] px-6 py-8 text-slate-400 text-sm leading-relaxed"
            >
              <Sparkles className="w-5 h-5 text-[#D4AF37] mb-3" />
              You haven&apos;t downloaded anything yet. Grab your first resource
              above — it&apos;ll show up here for easy re-access.
            </div>
          ) : (
            <ul
              data-testid="community-history-list"
              className="divide-y divide-white/10 border border-white/10 bg-[#0A1020]"
            >
              {history.map((h, i) => (
                <li
                  key={`${h.resource_slug}-${i}`}
                  className="flex items-center justify-between gap-4 px-5 py-4"
                >
                  <div>
                    <div className="text-white text-sm font-medium">
                      {RESOURCE_TITLE_BY_SLUG[h.resource_slug] || h.resource_slug}
                    </div>
                    <div className="text-slate-500 text-xs mt-1">
                      {new Date(h.downloaded_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      oneClickDownload(
                        h.resource_slug,
                        RESOURCE_TITLE_BY_SLUG[h.resource_slug] || h.resource_slug
                      )
                    }
                    className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#FDE047] font-bold"
                  >
                    Download Again
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Tip jar for members */}
      <section className="py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="border border-[#D4AF37]/30 bg-[#0A1020] px-6 sm:px-8 py-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
            <Coffee className="w-10 h-10 text-[#D4AF37] shrink-0" strokeWidth={1.4} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
                Fuel the Library
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Every guide, checklist, and ebook is free forever for members
                like you. If they&apos;ve saved you time or money, tips keep
                the next release coming.
              </p>
            </div>
            <BuyMeACoffeeButton
              variant="solid"
              label="Buy Me a Coffee"
              testid="community-bmc-btn"
              className="shrink-0"
            />
          </div>
        </div>
      </section>

      {/* Account settings */}
      <section className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-4">Account</SectionEyebrow>
          <div className="border border-white/10 bg-[#1A2B45] p-6 sm:p-8 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Signed in as
              </div>
              <div className="text-white text-base font-medium mt-1">{user?.email}</div>
              <div className="text-slate-500 text-xs mt-1">
                Sign-in method:{" "}
                {(user?.auth_providers || []).map((p) => (p === "password" ? "Email" : "Google")).join(" · ") || "—"}
              </div>
            </div>
            <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <Link
                to="/contact"
                className="text-[10px] uppercase tracking-[0.22em] text-slate-400 hover:text-[#D4AF37]"
              >
                Need password help? Contact us →
              </Link>
              <button
                type="button"
                onClick={logout}
                data-testid="community-logout-btn"
                className="inline-flex items-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
